import React, { useState } from 'react';
import DataFallback, { StaleDataBanner } from '../components/feedback/DataFallback';
import { classifyError, ERROR_KIND, TERMINAL_COPY } from '../utils/errorTaxonomy';

/**
 * Every loading and fallback state on one page, for reviewing the copy and the
 * sizing without having to provoke each failure against a real server.
 *
 * Development only — App.js mounts this route only when NODE_ENV !== 'production',
 * so it cannot ship.
 */

const KINDS = Object.values(ERROR_KIND);

function fakeError(kind) {
  return {
    kind,
    title: TERMINAL_COPY[kind].title,
    message: TERMINAL_COPY[kind].message,
    retryable: kind !== ERROR_KIND.QUOTA && kind !== ERROR_KIND.FORBIDDEN,
    status: 0,
  };
}

const box = {
  border: '1px dashed #cbd5e1',
  borderRadius: 8,
  background: '#fff',
  marginBottom: 16,
};

const label = {
  font: '600 12px/1.4 ui-monospace, monospace',
  color: '#64748b',
  padding: '6px 10px',
  background: '#f8fafc',
  borderBottom: '1px dashed #cbd5e1',
};

function Section({ title, children, note }) {
  return (
    <section style={{ marginBottom: 40 }}>
      <h2 style={{ font: '600 15px/1.4 Inter, sans-serif', color: '#0f172a', marginBottom: 4 }}>
        {title}
      </h2>
      {note && (
        <p style={{ font: '13px/1.5 Inter, sans-serif', color: '#64748b', margin: '0 0 12px' }}>
          {note}
        </p>
      )}
      {children}
    </section>
  );
}

export default function FallbackGallery() {
  const [retrying, setRetrying] = useState(false);
  const [width, setWidth] = useState(720);

  // A live countdown, restarted each time so the bar can be watched drain.
  const retryState = retrying
    ? {
        attempt: 2,
        maxAttempts: 4,
        delayMs: 8000,
        nextAttemptAt: Date.now() + 8000,
        reason: 'busy',
      }
    : null;

  return (
    <div style={{ padding: 32, maxWidth: 1100, margin: '0 auto', background: '#f1f5f9', minHeight: '100vh' }}>
      <h1 style={{ font: '700 22px/1.3 Inter, sans-serif', color: '#0f172a', marginBottom: 6 }}>
        Loading &amp; fallback states
      </h1>
      <p style={{ font: '14px/1.6 Inter, sans-serif', color: '#475569', marginBottom: 28 }}>
        Development only. Every state a customer can land on, so the wording and the
        sizing can be judged without provoking each failure for real.
      </p>

      <Section
        title="The copy, one card per cause"
        note="No status codes, and none of error / failed / invalid / broken — a build test enforces that."
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
          {KINDS.map((kind) => (
            <div key={kind} style={box}>
              <div style={label}>{kind}</div>
              <DataFallback variant="card" error={fakeError(kind)} onRetry={() => {}} />
            </div>
          ))}
        </div>
      </Section>

      <Section
        title="The retry, while it waits"
        note="A silent wait looks identical to a hang. The attempt count and the draining bar are what say the app is still working."
      >
        <button
          onClick={() => {
            setRetrying(false);
            setTimeout(() => setRetrying(true), 30);
          }}
          style={{
            padding: '8px 16px', background: '#0d9488', color: '#fff', border: 'none',
            borderRadius: 6, fontWeight: 600, cursor: 'pointer', marginBottom: 12,
          }}
        >
          Start an 8s countdown
        </button>
        <div style={box}>
          <div style={label}>variant="pane" · retryState set</div>
          <DataFallback
            variant="pane"
            error={fakeError(ERROR_KIND.BUSY)}
            retryState={retryState}
            onRetry={() => setRetrying(false)}
          />
        </div>
      </Section>

      <Section
        title="Stale data kept on screen"
        note="Shown above rows that are still readable while a refresh cannot get through."
      >
        <StaleDataBanner since={Date.now() - 3 * 60000} retryState={retryState} onRetry={() => {}} />
      </Section>

      <Section
        title="Does it fit the hole?"
        note="Drag the width. The same card drops its icon, then its body copy, as its container narrows — no props change."
      >
        <input
          type="range"
          min="140"
          max="900"
          value={width}
          onChange={(e) => setWidth(Number(e.target.value))}
          style={{ width: 320, marginBottom: 12, display: 'block' }}
        />
        <div style={{ font: '12px ui-monospace, monospace', color: '#64748b', marginBottom: 8 }}>
          container: {width}px
        </div>
        <div style={{ ...box, width, transition: 'width 0.1s' }}>
          <div style={label}>variant="card"</div>
          <DataFallback variant="card" error={fakeError(ERROR_KIND.BUSY)} onRetry={() => {}} />
        </div>
      </Section>

      <Section title="In a real table" note="Emits its own <tr><td colSpan> — a <div> inside <tbody> is invalid markup.">
        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 8 }}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              {['Order', 'Customer', 'Status', 'Total', 'Date'].map((h) => (
                <th key={h} style={{ padding: '10px 12px', textAlign: 'left', font: '600 12px Inter, sans-serif', color: '#64748b' }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <DataFallback variant="table" colSpan={5} error={fakeError(ERROR_KIND.DEPLOYING)} onRetry={() => {}} />
          </tbody>
        </table>
      </Section>

      <Section title="Stretching to a grid row" note="height:100% means the hole keeps the shape of the tile it replaced.">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, alignItems: 'stretch' }}>
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '1.25rem' }}>
            <div style={{ font: '11px Inter, sans-serif', color: '#64748b' }}>TOTAL ORDERS</div>
            <div style={{ font: '700 28px Inter, sans-serif', color: '#0f172a' }}>1,284</div>
          </div>
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10 }}>
            <DataFallback variant="card" error={fakeError(ERROR_KIND.BUSY)} onRetry={() => {}} />
          </div>
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '1.25rem' }}>
            <div style={{ font: '11px Inter, sans-serif', color: '#64748b' }}>DELIVERED</div>
            <div style={{ font: '700 28px Inter, sans-serif', color: '#0f172a' }}>11,536</div>
          </div>
        </div>
      </Section>

      <Section title="Straight from classifyError" note="What the real code path produces, not hand-written props.">
        {[
          ['TypeError, nginx confirmed down', classifyError(new TypeError('Failed to fetch'), { serverAlive: false })],
          ['502 from nginx', classifyError(Object.assign(new Error('x'), { status: 502 }))],
          ['429 with a usage body', classifyError(
            Object.assign(new Error('x'), { status: 429, data: { usage: { remaining: 0 } } }),
            { response: { status: 429, headers: { get: () => null } } }
          )],
        ].map(([name, err]) => (
          <div key={name} style={box}>
            <div style={label}>{name} → kind: {err.kind} · retryable: {String(err.retryable)}</div>
            <DataFallback variant="table" error={err} onRetry={() => {}} />
          </div>
        ))}
      </Section>
    </div>
  );
}
